export interface SignedInUser {
    id: string;
    email: string;
    displayName: string;
}

type JwtClaims = Record<string, unknown>;

const decodeBase64Url = (value: string): string => {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const binary = atob(padded);
    // atob yields one char per byte; re-encode as percent escapes so multi-byte UTF-8 (e.g. "Å") decodes correctly.
    const percentEncoded = Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`).join("");
    return decodeURIComponent(percentEncoded);
};

/**
 * Reads the payload of a JWT without validating it. Only use the result for display and
 * identification in the client; the API must validate the token itself.
 */
export const decodeJwtClaims = (token: string | null | undefined): JwtClaims | null => {
    const payload = token?.split(".")[1];
    if (!payload) {
        return null;
    }

    try {
        const claims = JSON.parse(decodeBase64Url(payload));
        return claims && typeof claims === "object" && !Array.isArray(claims) ? (claims as JwtClaims) : null;
    } catch {
        return null;
    }
};

const firstStringClaim = (claims: JwtClaims, names: string[]): string => {
    for (const name of names) {
        const value = claims[name];
        if (typeof value === "string" && value) {
            return value;
        }
    }
    return "";
};

/**
 * Builds the signed-in user from an Entra ID token (Teams SSO token, MSAL access token or ID token).
 * The "oid" claim is the Entra object id, which is the same value as the Teams context user id.
 */
export const getUserFromJwt = (token: string | null | undefined): SignedInUser | null => {
    const claims = decodeJwtClaims(token);
    if (!claims) {
        return null;
    }

    const id = firstStringClaim(claims, ["oid"]);
    if (!id) {
        return null;
    }

    const email = firstStringClaim(claims, ["upn", "preferred_username", "email", "unique_name"]);
    return {
        id,
        email,
        displayName: firstStringClaim(claims, ["name"]) || email,
    };
};
